// Global Instructions Rule Applied!
import {
  ACCOUNT_ROLE_CHOICE_PORTFOLIO_COMPANY,
  ACCOUNT_ROLE_CHOICE_DEAD,
  ACCOUNT_ROLE_CHOICE_EXITED,
  ACCOUNT_ROLE_CHOICE_CO_INVESTOR,
  ACCOUNT_ROLE_CHOICE_LP,
  ACCOUNT_ROLE_CHOICE_PIPELINE_COMPANY,
  ACCOUNT_ROLE_CHOICE_PASSED,
  ACCOUNT_ROLE_CHOICE_PASSED_TRACK,
  ACCOUNT_ROLE_CHOICE_LOST
} from '../../../state_manager/globals.ts'
import { AccountRecordFields } from '../../../state_manager/data_models/account_record.ts'

// The account-role choices that mark an account as a portfolio-type company.
// These are the only accounts that show the portfolio sections — Latest
// Valuation, Investment Summary and Holdings — on both the desktop account
// form and the mobile account screen.
export const PORTFOLIO_SECTION_ROLE_IDS: string[] = [
  ACCOUNT_ROLE_CHOICE_PORTFOLIO_COMPANY,
  ACCOUNT_ROLE_CHOICE_DEAD,
  ACCOUNT_ROLE_CHOICE_EXITED
]

// The account-role choices that mark an account as one the firm has EVALUATED
// — the portfolio set above plus every origination outcome. These are the
// accounts that show the Deals & Rounds tab: a deal or a round exists for a
// company we looked at, whether or not we ended up holding it. An advisory
// firm, an attorney or a plain LP has no deal history to show.
export const DEAL_SECTION_ROLE_IDS: string[] = [
  ...PORTFOLIO_SECTION_ROLE_IDS,
  ACCOUNT_ROLE_CHOICE_PIPELINE_COMPANY,
  ACCOUNT_ROLE_CHOICE_PASSED,
  ACCOUNT_ROLE_CHOICE_PASSED_TRACK,
  ACCOUNT_ROLE_CHOICE_LOST
]

/** The account's role-choice ids, tolerating both shapes the record can
 *  carry: an array of objects keyed `account_roles__account_role_choice_id`,
 *  or a plain array of id strings. */
function roleChoiceIds(record: Record<string, any> | null | undefined): string[] {
  if (!record) return []
  const roles = record[AccountRecordFields.prog__account_roles] || []
  if (!Array.isArray(roles)) return []
  return roles
    .map((r: any) => (typeof r === 'string' ? r : r?.account_roles__account_role_choice_id))
    .filter(Boolean)
}

/**
 * True when the account carries at least one portfolio-type role choice
 * (Portfolio Company / Exited / Dead). Reads `prog__account_roles`, tolerating
 * both the array-of-objects shape (each carrying
 * `account_roles__account_role_choice_id`) and a plain array of id strings.
 *
 * Single source of truth for gating the portfolio-only sections/tabs:
 *  - desktop Holdings, Cap Table and Legal Rights tabs (Full.tsx),
 *  - desktop Latest Valuation + Investment Summary (DashboardTab.tsx),
 *  - mobile Valuation / Investment summary / Holdings (CompanyScreen.tsx).
 */
export function accountHasPortfolioRole(record: Record<string, any> | null | undefined): boolean {
  return roleChoiceIds(record).some((id: string) => PORTFOLIO_SECTION_ROLE_IDS.includes(id))
}

/**
 * True when the account carries at least one role choice that implies deal
 * history — the portfolio set plus Pipeline / Passed / Passed - Track / Lost
 * (owner, 2026-09-03). Gates the account form's Deals & Rounds tab.
 */
export function accountHasDealRole(record: Record<string, any> | null | undefined): boolean {
  return roleChoiceIds(record).some((id: string) => DEAL_SECTION_ROLE_IDS.includes(id))
}

/**
 * True when the account carries the Co-investor role choice (added by the
 * role engine when the account is a linked holder on a reviewed cap-table
 * version, or set manually). Gates the account form's Co-investments tab.
 */
/**
 * True when the account carries the LP role choice (active or target
 * limited partner — the role the relationship-manager agent follows). Gates
 * the LP agent panels on the Dashboard and the LP Agent tab (Full.tsx).
 * The id is environment-stable (cfg.account_role_rules.signal_key =
 * 'is_prospect' resolves to the same choice row in every environment).
 */
export function accountHasLpRole(record: Record<string, any> | null | undefined): boolean {
  return roleChoiceIds(record).includes(ACCOUNT_ROLE_CHOICE_LP)
}

export function accountHasCoInvestorRole(record: Record<string, any> | null | undefined): boolean {
  return roleChoiceIds(record).includes(ACCOUNT_ROLE_CHOICE_CO_INVESTOR)
}
